import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronDown, ShieldCheck, Ship, Anchor, Package, Plane, Truck, Train, Calculator, Tag,
} from 'lucide-react'
import IntegrationCard from './IntegrationCard'

const ICONS = {
  shield: ShieldCheck,
  ship: Ship,
  anchor: Anchor,
  package: Package,
  plane: Plane,
  truck: Truck,
  train: Train,
  calculator: Calculator,
  tag: Tag,
}

export default function CategorySection({
  category,
  items,
  view,
  defaultOpen = true,
  onAction,
}) {
  const [open, setOpen] = useState(defaultOpen)

  if (!items.length) return null

  return (
    <section
      id={`category-${category.id}`}
      className="overflow-hidden rounded-2xl border border-slate-500 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)] dark:border-slate-600 dark:bg-slate-900"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/60 sm:px-6"
        aria-expanded={open}
      >
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-50 to-slate-50 text-brand-600 ring-1 ring-slate-300/60 dark:from-brand-500/10 dark:to-slate-800 dark:text-brand-400 dark:ring-slate-600/60`}
          aria-hidden
        >
          {(() => {
            const Icon = ICONS[category.icon] || ShieldCheck
            return <Icon className="h-5 w-5" />
          })()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-white sm:text-[17px]">{category.title}</h2>
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {items.length}
            </span>
          </div>
          {category.helper && (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 sm:text-[13px]">{category.helper}</p>
          )}
        </div>
        <ChevronDown
          className={[
            'h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 dark:text-slate-500',
            open ? 'rotate-180' : '',
          ].join(' ')}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-200 px-4 pb-5 pt-4 dark:border-slate-700 sm:px-6">
              {view === 'grid' ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {items.map((item) => (
                    <IntegrationCard key={item.id} item={item} view={view} onAction={onAction} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {items.map((item) => (
                    <IntegrationCard key={item.id} item={item} view={view} onAction={onAction} />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
